// Role → tab permissions map for the web-clinic workspace.
//
// The five UI-visible roles (see @pamosi/shared CLINIC_MEMBER_ROLE_OPTIONS)
// split into four access shapes after Phase 1a of the revised EMR
// framework (2026-10-03):
//   admin        → everything, including Reception
//   receptionist → Reception + Patient records (card creation / lookup;
//                  no clinical note access)
//   doctor       ┐
//   nurse        ├→ clinical work (Work list + Patient records)
//   pharmacist   → Pharmacy only
//
// The legacy 'provider' value (kept in the DB enum for pre-Week-6 rows) is
// treated as doctor — same clinical-work access shape it always had before
// the split.
//
// Server-side enforcement lives in lib/clinic_authz.ts on the API; this file
// exists so the UI can hide off-role tabs and land each role on the tab they
// actually use, matching the server's action matrix.

import type { ClinicMemberRole } from '@pamosi/shared';

export type ClinicTab =
    | 'reception'
    | 'queues'
    | 'work-list'
    | 'child-health'
    | 'payments'
    | 'pharmacy'
    | 'procedures'
    | 'labs'
    | 'team'
    | 'patient-records'
    | 'shift-reports'
    | 'activity';

export interface RolePermissions {
    tabs: ReadonlyArray<ClinicTab>;
    // Landing route the app pushes them into after sign-in and whenever
    // they hit an off-role URL.
    landing: string;
}

const ADMIN_PERMS: RolePermissions = {
    tabs: ['reception', 'queues', 'work-list', 'procedures', 'labs', 'pharmacy', 'payments', 'team', 'patient-records', 'child-health', 'shift-reports', 'activity'],
    landing: '/work-list',
};

// Reception portal — card types, registration, family/payer, duplicate
// checks. Framework §07 draws a hard boundary between reception and the
// clinical portals; receptionists get patient-records (lookup) but not the
// work-list (clinical queue) or the clinical note surfaces.
const RECEPTION_PERMS: RolePermissions = {
    tabs: ['reception', 'queues', 'patient-records', 'child-health', 'payments'],
    landing: '/reception',
};

const CLINICAL_PERMS: RolePermissions = {
    // Doctors and nurses share the same surface: the work list to move
    // patients through the day, department queues to pick up new patients,
    // patient records for history lookup, and the procedures queue (Phase
    // 2e). procedures is visible to both so a doctor can glance at the
    // nurse's execution backlog even though only nurses can log fulfillment.
    // shift-reports added Phase 4 — nurses author handovers; doctors read.
    tabs: ['queues', 'work-list', 'procedures', 'labs', 'patient-records', 'child-health', 'shift-reports'],
    landing: '/queues',
};

const PHARMACY_PERMS: RolePermissions = {
    tabs: ['queues', 'pharmacy'],
    landing: '/pharmacy',
};

export function getRolePermissions(role: ClinicMemberRole): RolePermissions {
    switch (role) {
        case 'admin':
            return ADMIN_PERMS;
        case 'lab_technician':
            return { tabs: ['labs'], landing: '/labs' };
        case 'pharmacist':
            return PHARMACY_PERMS;
        case 'receptionist':
            return RECEPTION_PERMS;
        case 'doctor':
        case 'nurse':
        case 'provider': // legacy — treat as doctor
            return CLINICAL_PERMS;
    }
}

export function canAccessTab(role: ClinicMemberRole, tab: ClinicTab): boolean {
    return getRolePermissions(role).tabs.includes(tab);
}
